/**
 * LoginService.js - 登录功能封装
 * 基于 Vuex 全局状态管理，提供登录/退出/状态检测
 * 用法：在组件中 import 后调用方法，状态通过 Vuex 共享
 */

import { jsonp } from 'vue-jsonp';

// ==================== 配置 ====================
const CONFIG = {
    INIT_LOGIN_URL: 'https://user.drcnet.com.cn/userapi/loginController/initLoginP',
    LOGOUT_URL: 'https://user.drcnet.com.cn/userapi/loginController/logOutAllP',
    LOGIN_PAGE_URL: 'https://user.drcnet.com.cn/#/login',
};

// ==================== 内部工具 ====================
function getRandom() {
    return Date.now() + '_' + Math.random().toFixed(6);
}

function sendLoginTrack() {
    try {
        if (typeof LogCollection !== 'undefined' && LogCollection.send) {
            LogCollection.send(6);
        }
    } catch (e) {}
}

/**
 * 统一更新用户状态（Vuex + localStorage）
 * @param {Object} store - Vuex store 实例
 * @param {Object} res - 登录接口响应数据
 */
function updateUserState(store, res) {
    if (res.Logined === 1 && res.UserToken) {
        // 已登录
        store.commit('setUserLoginState', true);
        store.commit('updateUserToken', res.UserToken);
        store.commit('updateUserName', res.UserName || '');
        store.commit('setUserAdv', res.IsAdv || false);
        localStorage.setItem('userToken', res.UserToken);
        localStorage.setItem('userName', res.UserName || '');
    } else {
        // 未登录
        clearUserState(store);
    }
}

/**
 * 清除用户状态
 * @param {Object} store - Vuex store 实例
 */
function clearUserState(store) {
    store.commit('setUserLoginState', false);
    store.commit('updateUserToken', '');
    store.commit('updateUserName', '');
    store.commit('setUserAdv', false);
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
}

// ==================== 核心功能 ====================

/**
 * 检测登录状态（初始化时调用）
 * @param {Object} store - Vuex store 实例
 * @returns {Promise<Object>}
 */
export function checkLoginState(store) {
    return new Promise((resolve, reject) => {
        jsonp(CONFIG.INIT_LOGIN_URL, { flag: 'initp' })
            .then((res) => {
                if (res.UserToken !== undefined) {
                    updateUserState(store, res);
                    resolve({
                        loggedIn: res.Logined === 1,
                        userName: res.UserName || '',
                        userToken: res.UserToken || '',
                        isAdv: res.IsAdv || false,
                    });
                } else {
                    clearUserState(store);
                    resolve({ loggedIn: false, userName: '', userToken: '', isAdv: false });
                }
            })
            .catch((err) => {
                console.error('登录状态检测失败:', err);
                clearUserState(store);
                reject(err);
            });
    });
}

/**
 * 退出登录
 * @param {Object} store - Vuex store 实例
 * @param {Object} router - Vue Router 实例（可选，用于刷新页面）
 * @returns {Promise}
 */
export function logout(store, router) {
    return new Promise((resolve, reject) => {
        jsonp(CONFIG.LOGOUT_URL, { randomId: getRandom() })
            .then((res) => {
                clearUserState(store);
                if (router) {
                    router.go(0);
                }
                resolve(res);
            })
            .catch((err) => {
                clearUserState(store);
                if (router) {
                    router.go(0);
                }
                reject(err);
            });
    });
}

/**
 * 跳转登录页面
 * @param {string} [redirectUrl] - 登录成功后跳转回来的地址
 */
export function goToLogin(redirectUrl) {
    const currentUrl = redirectUrl || window.location.href;
    const encodedUrl = currentUrl.replace(/\?/g, '~~').replace(/&/g, '^');
    const loginUrl = CONFIG.LOGIN_PAGE_URL + '?gourl=' + encodedUrl;
    sendLoginTrack();
    window.location.href = loginUrl;
}

/**
 * 获取当前登录状态（同步，基于 localStorage）
 * @returns {Object}
 */
export function getLoginState() {
    const token = localStorage.getItem('userToken');
    const name = localStorage.getItem('userName');
    return {
        isLoggedIn: !!token && token !== '',
        userName: name || '',
        userToken: token || '',
    };
}

// ==================== 默认导出 ====================
export default {
    checkLoginState,
    logout,
    goToLogin,
    getLoginState,
};